import React, { useState, useEffect } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Button,
} from '@nextui-org/react';
import { fetchCategoryStats, CategoryStats } from './utils';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface EnhancedCategoryStats extends CategoryStats {
  instance_count_percentage: number;
  sales_percentage: number;
  average_ticket: number;
}

const CategoryStatsTable: React.FC = () => {
  const [categoryStats, setCategoryStats] = useState<EnhancedCategoryStats[]>(
    [],
  );
  const [startDate, setStartDate] = useState('2023-01-01');
  const [endDate, setEndDate] = useState('2024-12-31');
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const loadCategoryStats = async () => {
    const data = await fetchCategoryStats(startDate, endDate);
    const totalInstances = data.reduce(
      (sum, stat) =>
        sum + (stat.category !== 'TOTAL' ? stat.instance_count : 0),
      0,
    );
    const totalSales = data.reduce(
      (sum, stat) => sum + (stat.category !== 'TOTAL' ? stat.total_sales : 0),
      0,
    );

    const enhancedData: EnhancedCategoryStats[] = data.map((stat) => ({
      ...stat,
      instance_count_percentage:
        stat.category !== 'TOTAL'
          ? (stat.instance_count / totalInstances) * 100
          : 100,
      sales_percentage:
        stat.category !== 'TOTAL' ? (stat.total_sales / totalSales) * 100 : 100,
      average_ticket:
        stat.instance_count > 0 ? stat.total_sales / stat.instance_count : 0,
    }));

    setCategoryStats(enhancedData);
  };

  useEffect(() => {
    loadCategoryStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleDateSubmit = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  const exportToExcel = () => {
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const ws = XLSX.utils.json_to_sheet(
      categoryStats.map((stat) => ({
        Categoría: stat.category,
        'Total de Tareas': stat.instance_count,
        '% de Tareas':
          stat.category !== 'TOTAL'
            ? `${stat.instance_count_percentage.toFixed(2)}%`
            : '100%',
        'Ventas Totales': stat.total_sales,
        '% de Ventas':
          stat.category !== 'TOTAL'
            ? `${stat.sales_percentage.toFixed(2)}%`
            : '100%',
        'Ticket Promedio': stat.average_ticket,
      })),
    );

    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    saveAs(data, `category_stats_${startDate}_${endDate}${fileExtension}`);
  };

  return (
    <div>
      <div className="mb-6 p-4 border rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-3">
          Seleccionar Rango de Fechas
        </h3>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-grow">
            <Input
              label="Fecha de Inicio"
              type="date"
              value={tempStartDate}
              onChange={(e) => setTempStartDate(e.target.value)}
              startContent={
                <Icon icon="mdi:calendar" className="text-gray-400" />
              }
              className=""
              aria-label="Fecha de Inicio"
            />
          </div>
          <div className="flex-grow">
            <Input
              label="Fecha de Fin"
              type="date"
              value={tempEndDate}
              onChange={(e) => setTempEndDate(e.target.value)}
              startContent={
                <Icon icon="mdi:calendar" className="text-gray-400" />
              }
              className=""
              aria-label="Fecha de Fin"
            />
          </div>
          <Button
            color="primary"
            onClick={handleDateSubmit}
            className="px-6 shrink-0"
          >
            Aplicar Rango
          </Button>
        </div>
      </div>

      <div className="mb-6 p-4 border rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-3">Otras Opciones</h3>
        <Button
          color="success"
          onClick={exportToExcel}
          className="px-6 shrink-0"
          startContent={<Icon icon="mdi:microsoft-excel" className="text-xl" />}
        >
          Exportar a Excel
        </Button>
      </div>

      <Table aria-label="Tabla de Estadísticas por Categoría">
        <TableHeader>
          <TableColumn>Categoría</TableColumn>
          <TableColumn>Total de Tareas</TableColumn>
          <TableColumn>% de Tareas</TableColumn>
          <TableColumn>Ventas Totales</TableColumn>
          <TableColumn>% de Ventas</TableColumn>
          <TableColumn>Ticket Promedio</TableColumn>
        </TableHeader>
        <TableBody>
          {categoryStats.map((stat) => (
            <TableRow key={stat.category}>
              <TableCell>{stat.category}</TableCell>
              <TableCell>{stat.instance_count}</TableCell>
              <TableCell>
                {stat.instance_count_percentage.toFixed(2)}%
              </TableCell>
              <TableCell>
                $
                {stat.total_sales.toLocaleString('es-ES', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TableCell>
              <TableCell>{stat.sales_percentage.toFixed(2)}%</TableCell>
              <TableCell>
                $
                {stat.average_ticket.toLocaleString('es-ES', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default CategoryStatsTable;
